import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Button, CircularProgress, Alert } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import plusIcon from '../../assets/icons/plus.svg';
import editIcon from '../../assets/icons/edit.svg';
import trashIcon from '../../assets/icons/trash.svg';
import chevronDownIcon from '../../assets/icons/chevron-down.svg';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import checkIcon from '../../assets/icons/check.svg';
import CategoryFilters from './CategoryFilters';
import Rows from '../../components/ui/Rows';
import CircularIconButton from '../../components/ui/CircularIconButton';
import EffectPill from './EffectPill';
import Toggle from '../../components/ui/Toggle';
import CustomSnackbar from '../../components/ui/Snackbar';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import CreationModal from '../../components/ui/CreationModal';
import { categoryAPI, caisseAPI } from '../../services/api';
import { isWithdrawalCategory } from '../../utils/categoryUtils';

const Categories = () => {
  const { user } = useAuth();
  
  const [categories, setCategories] = useState([]); // Display filtered data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, category: null });
  const [statusDialog, setStatusDialog] = useState({ open: false, category: null, action: null, formData: null });
  const [notificationDialog, setNotificationDialog] = useState({ open: false, category: null, action: null });
  const [pointageDialog, setPointageDialog] = useState({ open: false, category: null, action: null });
  const [createModal, setCreateModal] = useState({ open: false, errors: {} });
  const [editModal, setEditModal] = useState({ open: false, category: null, errors: {} });
  const [editConfirmDialog, setEditConfirmDialog] = useState({ open: false, changes: null });
  const [caisses, setCaisses] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    total: 0
  });
  
  const [filters, setFilters] = useState({
    search: '',
    caisseId: '',
    caisseFactor: '',
    coffreFactor: '',
    active: '',
    notifyManager: '',
    requiresPointage: ''
  });
  
  // Sorting state
  const [sortBy, setSortBy] = useState('label');
  const [sortDirection, setSortDirection] = useState('ASC');

  const contentContainerSx = {
    width: '100%',
    margin: '0 auto',
    paddingLeft: { xs: '26px', sm: '42px' },
    paddingRight: { xs: '26px', sm: '42px' }
  };

  const stickyOuterSx = { ...contentContainerSx };
  
  const [isStuck, setIsStuck] = useState(false);
  const STICKY_TOP_OFFSET = 76;


  // Apply filters to the original data

  // Main fetch function - uses backend pagination and filtering
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Build query parameters for backend
      const params = {
        page: pagination.page,
        size: pagination.size,
        sortBy: sortBy,
        sortDirection: sortDirection,
        ...(filters.search && { searchTerm: filters.search }),
        ...(filters.caisseId && { caisseId: filters.caisseId }),
        ...(filters.caisseFactor && { caisseFactor: filters.caisseFactor }),
        ...(filters.coffreFactor && { coffreFactor: filters.coffreFactor }),
        ...(filters.active && { active: filters.active === 'true' }),
        ...(filters.notifyManager && { notifyManager: filters.notifyManager === 'true' }),
        ...(filters.requiresPointage && { requiresPointage: filters.requiresPointage === 'true' })
      };
      
      const response = await categoryAPI.getAll(params);
      const categories = response.data.content || [];
      const totalElements = response.data.totalElements || 0;
      
      setCategories(categories);
      setPagination(prev => ({
        ...prev,
        total: totalElements
      }));
      
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Erreur lors du chargement des catégories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, filters, sortBy, sortDirection]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSort = (field, direction) => {
    setSortBy(field);
    setSortDirection(direction);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  // Simple scroll listener for background color change
  useEffect(() => {
    const handleScroll = () => {
      setIsStuck(window.scrollY > STICKY_TOP_OFFSET);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Load caisses for the create modal
  useEffect(() => {
    const loadCaisses = async () => {
      try {
        const response = await caisseAPI.getForFilters();
        const caisses = Array.isArray(response.data) ? response.data : [];
        setCaisses(caisses);
      } catch (error) {
        console.error('Error loading caisses:', error);
      }
    };
    loadCaisses();
  }, []);

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 0 })); // Reset to first page when filters change
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({
      search: '',
      caisseId: '',
      caisseFactor: '',
      coffreFactor: '',
      active: '',
      notifyManager: '',
      requiresPointage: ''
    });
    setPagination(prev => ({ ...prev, page: 0 })); // Reset to first page when filters are reset
  }, []);

  // Get paginated data (now handled by backend)
  const getPaginatedData = useCallback(() => {
    return categories; // Backend already returns paginated data
  }, [categories]);

  // Field configuration for creation modal
  const getCategoryFields = () => {
    return [
      {
        name: 'label',
        type: 'text',
        placeholder: 'Libellé de la catégorie',
        required: true,
        validation: (value) => {
          if (value && value.trim().toLowerCase() === 'sortie coffre') {
            return 'Impossible de créer cette catégorie car elle est créée automatiquement lors de la création d\'une caisse';
          }
          return null;
        }
      },
      {
        name: 'caisseFactor',
        type: 'select',
        placeholder: 'Effet Caisse',
        required: true,
        options: [
          { value: 1, label: 'Augmente (+)' },
          { value: 0, label: 'Aucun effet (0)' },
          { value: -1, label: 'Diminue (-)' }
        ]
      },
      {
        name: 'coffreFactor',
        type: 'select',
        placeholder: 'Effet Coffre',
        required: true,
        options: [
          { value: 1, label: 'Augmente (+)' },
          { value: 0, label: 'Aucun effet (0)' },
          { value: -1, label: 'Diminue (-)' }
        ]
      },
      {
        name: 'caisseIds',
        type: 'multiselect',
        placeholder: 'Caisse',
        required: true,
        options: caisses.map(caisse => ({ value: caisse.id, label: caisse.name }))
      },
      {
        name: 'notifyManager',
        type: 'select',
        placeholder: 'Notifications Manager',
        required: true,
        options: [
          { value: true, label: 'Activé' },
          { value: false, label: 'Désactivé' }
        ]
      },
      {
        name: 'requiresPointage',
        type: 'select',
        placeholder: 'Nécessite Pointage',
        required: true,
        options: [
          { value: true, label: 'Activé' },
          { value: false, label: 'Désactivé' }
        ]
      }
    ];
  };

  // Field configuration for edit modal (static array like Caisses)
  const editCategoryFields = [
    {
      name: 'label',
      type: 'text',
      placeholder: 'Libellé de la catégorie',
      required: true
    },
    {
      name: 'caisseFactor',
      type: 'select',
      placeholder: 'Effet Caisse',
      required: true,
      options: [
        { value: 1, label: 'Augmente (+)' },
        { value: 0, label: 'Aucun effet (0)' },
        { value: -1, label: 'Diminue (-)' }
      ]
    },
    {
      name: 'coffreFactor',
      type: 'select',
      placeholder: 'Effet Coffre',
      required: true,
      options: [
        { value: 1, label: 'Augmente (+)' },
        { value: 0, label: 'Aucun effet (0)' },
        { value: -1, label: 'Diminue (-)' }
      ]
    },
    {
      name: 'notifyManager',
      type: 'select',
      placeholder: 'Notifications Manager',
      required: true,
      options: [
        { value: true, label: 'Activé' },
        { value: false, label: 'Désactivé' }
      ]
    },
    {
      name: 'requiresPointage',
      type: 'select',
      placeholder: 'Nécessite Pointage',
      required: true,
      options: [
        { value: true, label: 'Activé' },
        { value: false, label: 'Désactivé' }
      ]
    }
  ];

  // Handle create category
  const handleCreateCategory = () => {
    setCreateModal({ open: true, errors: {} });
  };

  // Handle submit edit category
  const handleSubmitEditCategory = async (formData) => {
    if (!editModal.category) return;

    // Check if there are any changes
    const originalData = {
      label: editModal.category.label,
      caisseFactor: editModal.category.caisseFactor,
      coffreFactor: editModal.category.coffreFactor,
      notifyManager: editModal.category.notifyManager,
      requiresPointage: editModal.category.requiresPointage,
      active: editModal.category.active
    };

    const hasChanges = Object.keys(formData).some(key => {
      return formData[key] !== originalData[key];
    });

    if (!hasChanges) {
      setEditModal({ open: false, category: null, errors: {} });
      return;
    }

    // Check if only active status changed
    const onlyActiveChanged = Object.keys(formData).every(key => {
      if (key === 'active') {
        return formData[key] !== originalData[key];
      }
      return formData[key] === originalData[key];
    });

    if (onlyActiveChanged) {
      // Show specific activate/deactivate dialog
      const action = formData.active ? 'activate' : 'deactivate';
      setStatusDialog({ open: true, category: editModal.category, action, formData });
      return;
    }

    // Show general confirmation dialog for other changes
    setEditConfirmDialog({ open: true, changes: formData });
  };

  // Confirm edit changes
  const confirmEditChanges = async () => {
    if (!editConfirmDialog.changes) return;

    setActionLoading(true);
    try {
      const categoryData = {
        label: editConfirmDialog.changes.label,
        caisseFactor: editConfirmDialog.changes.caisseFactor,
        coffreFactor: editConfirmDialog.changes.coffreFactor,
        notifyManager: editConfirmDialog.changes.notifyManager,
        requiresPointage: editConfirmDialog.changes.requiresPointage,
        active: editConfirmDialog.changes.active
      };

      await categoryAPI.update(editModal.category.id, categoryData);
      setSnackbar({ open: true, message: 'Catégorie modifiée avec succès', severity: 'success' });
      setEditModal({ open: false, category: null, errors: {} });
      setEditConfirmDialog({ open: false, changes: null });
      // Refresh the data and reapply filters
      await fetchCategories();
    } catch (err) {
      console.error('Error updating category:', err);
      if (err.response?.data?.errors) {
        setEditModal(prev => ({ ...prev, errors: err.response.data.errors }));
      } else {
        setSnackbar({ open: true, message: 'Erreur lors de la modification de la catégorie', severity: 'error' });
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Handle submit create category
  const handleSubmitCreateCategory = async (formData) => {
    setActionLoading(true);
    try {
      // Transform form data for bulk API
      const categoryData = {
        label: formData.label,
        caisseIds: formData.caisseIds,
        caisseFactor: formData.caisseFactor,
        coffreFactor: formData.coffreFactor,
        notifyManager: formData.notifyManager,
        requiresPointage: formData.requiresPointage
      };

      const result = await categoryAPI.createBulk(categoryData);
      
      if (result.data.success) {
        const createdCount = result.data.createdCount;
        setSnackbar({ 
          open: true, 
          message: `${createdCount} catégorie(s) créée(s) avec succès`, 
          severity: 'success' 
        });
        setCreateModal({ open: false, errors: {} });
        // Refresh the data and reapply filters
        await fetchCategories();
      } else {
        throw new Error('Bulk creation failed');
      }
    } catch (err) {
      console.error('Error creating category:', err);
      if (err.response?.data?.errors) {
        setCreateModal(prev => ({ ...prev, errors: err.response.data.errors }));
      } else {
        setSnackbar({ open: true, message: 'Erreur lors de la création de la catégorie', severity: 'error' });
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Handle edit category
  const handleEditCategory = (category) => {
    setEditModal({ open: true, category, errors: {} });
  };

  // Handle toggle status
  const handleToggleStatus = (category) => {
    const action = category.active ? 'deactivate' : 'activate';
    setStatusDialog({ open: true, category, action });
  };

  // Confirm status change
  const confirmStatusChange = async () => {
    if (!statusDialog.category) return;
    
    try {
      if (statusDialog.action === 'activate') {
        await categoryAPI.activate(statusDialog.category.id);
        setSnackbar({ open: true, message: 'Catégorie activée avec succès', severity: 'success' });
      } else {
        await categoryAPI.deactivate(statusDialog.category.id);
        setSnackbar({ open: true, message: 'Catégorie désactivée avec succès', severity: 'success' });
      }
      
      // If this came from edit modal, close it and refresh data
      if (statusDialog.formData) {
        setEditModal({ open: false, category: null, errors: {} });
        await fetchCategories();
      } else {
        // Update both original and filtered data
        const updateCategory = (category) => 
          category.id === statusDialog.category.id 
            ? { ...category, active: statusDialog.action === 'activate' }
            : category;
        
        setCategories(prev => prev.map(updateCategory));
      }
      
      setStatusDialog({ open: false, category: null, action: null, formData: null });
    } catch (err) {
      console.error('Error toggling category status:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la modification du statut', severity: 'error' });
    }
  };

  // Handle toggle notifications
  const handleToggleNotifications = (category) => {
    const action = category.managerPersonalPreference ? 'deactivate' : 'activate';
    setNotificationDialog({ open: true, category, action });
  };

  // Confirm notification change
  const confirmNotificationChange = async () => {
    if (!notificationDialog.category) return;
    
    try {
      await categoryAPI.update(notificationDialog.category.id, {
        notifyManager: notificationDialog.action === 'activate'
      });
      
      const actionText = notificationDialog.action === 'activate' ? 'activées' : 'désactivées';
      setSnackbar({ open: true, message: `Notifications ${actionText} avec succès`, severity: 'success' });
      
      // Update both original and filtered data
      const updateCategory = (category) => 
        category.id === notificationDialog.category.id 
          ? { ...category, managerPersonalPreference: notificationDialog.action === 'activate' }
          : category;
      
      setCategories(prev => prev.map(updateCategory));
      
      setNotificationDialog({ open: false, category: null, action: null });
    } catch (err) {
      console.error('Error toggling notifications:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la modification des notifications', severity: 'error' });
    }
  };

  // Handle toggle pointage
  const handleTogglePointage = (category) => {
    const action = category.requiresPointage ? 'deactivate' : 'activate';
    setPointageDialog({ open: true, category, action });
  };

  // Confirm pointage change
  const confirmPointageChange = async () => {
    if (!pointageDialog.category) return;
    
    try {
      await categoryAPI.update(pointageDialog.category.id, {
        requiresPointage: pointageDialog.action === 'activate'
      });
      
      const actionText = pointageDialog.action === 'activate' ? 'activé' : 'désactivé';
      setSnackbar({ open: true, message: `Pointage ${actionText} avec succès`, severity: 'success' });
      
      // Update both original and filtered data
      const updateCategory = (category) => 
        category.id === pointageDialog.category.id 
          ? { ...category, requiresPointage: pointageDialog.action === 'activate' }
          : category;
      
      setCategories(prev => prev.map(updateCategory));
      
      setPointageDialog({ open: false, category: null, action: null });
    } catch (err) {
      console.error('Error toggling pointage:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la modification du pointage', severity: 'error' });
    }
  };

  // Handle delete category
  const handleDeleteCategory = (category) => {
    setDeleteDialog({ open: true, category });
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!deleteDialog.category) return;
    
    try {
      await categoryAPI.delete(deleteDialog.category.id);
      setSnackbar({ open: true, message: 'Catégorie supprimée avec succès', severity: 'success' });
      setDeleteDialog({ open: false, category: null });
      // Refresh the data and reapply filters
      await fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      
      // Check if it's a conflict error (has transactions)
      if (err.response?.status === 409) {
        // Show confirmation dialog for disabling instead
        setDeleteDialog({ 
          open: true, 
          category: deleteDialog.category,
          mode: 'disable' // New mode for disable confirmation
        });
      } else {
        setSnackbar({ open: true, message: 'Erreur lors de la suppression de la catégorie', severity: 'error' });
      }
    }
  };

  // Handle disable instead of delete
  const confirmDisable = async () => {
    if (!deleteDialog.category) return;
    
    try {
      await categoryAPI.deactivate(deleteDialog.category.id);
      setSnackbar({ open: true, message: 'Catégorie désactivée avec succès', severity: 'success' });
      setDeleteDialog({ open: false, category: null });
      // Refresh the data and reapply filters
      await fetchCategories();
    } catch (err) {
      console.error('Error disabling category:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la désactivation de la catégorie', severity: 'error' });
    }
  };

  // Table columns configuration
  const columns = [
    { 
      key: 'label', 
      label: 'Libellé', 
      width: '24%',
      sortable: true,
      render: (value, row) => (
        <span style={{
          textAlign: 'left',
          font: 'normal normal 600 16px/19px Montserrat',
          letterSpacing: '0px',
          color: '#38485C',
          textTransform: 'none',
          opacity: 0.88
        }}>
          {row.label}
          {isWithdrawalCategory(row) && (
            <span style={{
              marginLeft: '8px',
              color: '#919191',
              fontSize: '12px',
              fontWeight: 600
            }}>(Système)</span>
          )}
        </span>
      )
    },
    { 
      key: 'Caisse', 
      label: 'Caisse', 
      width: '17%',
      sortable: true,
      render: (value, row) => (
        <span style={{
          textAlign: 'left',
          font: 'normal normal 600 16px/19px Montserrat',
          letterSpacing: '0px',
          color: '#38485C',
          textTransform: 'none',
          opacity: 0.88
        }}>
          {row.caisse?.name || 'N/A'}
        </span>
      )
    },
    { 
      key: 'caisseFactor', 
      label: 'Effet Caisse', 
      width: '12%',
      sortable: true,
      render: (value, row) => <EffectPill value={parseInt(row.caisseFactor)} />
    },
    { 
      key: 'coffreFactor', 
      label: 'Effet Coffre', 
      width: '12%',
      sortable: true,
      render: (value, row) => <EffectPill value={parseInt(row.coffreFactor)} />
    },
    { 
      key: 'active', 
      label: 'Statut', 
      width: '9%',
      sortable: true,
      render: (value, row) => (
        <div style={{ marginLeft: '-12px' }}>
          <Toggle
            checked={row.active}
            onChange={() => handleToggleStatus(row)}
            label={row.active ? 'Actif' : 'Inactif'}
          />
        </div>
      )
    },
    { 
      key: 'notifyManager', 
      label: 'Notifications', 
      width: '9%',
      sortable: true,
      render: (value, row) => (
        <div style={{ marginLeft: '-12px' }}>
          <Toggle
            checked={row.managerPersonalPreference}
            onChange={() => handleToggleNotifications(row)}
            label={row.managerPersonalPreference ? 'Activé' : 'Désactivé'}
          />
        </div>
      )
    },
    { 
      key: 'requiresPointage', 
      label: 'Pointage', 
      width: '9%',
      sortable: true,
      render: (value, row) => (
        <div style={{ marginLeft: '-12px' }}>
          <Toggle
            checked={row.requiresPointage}
            onChange={() => handleTogglePointage(row)}
            label={row.requiresPointage ? 'Activé' : 'Désactivé'}
          />
        </div>
      )
    },
    { 
      key: 'actions', 
      label: 'Actions', 
      width: '8%',
      sortable: false,
      render: (value, row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isWithdrawalCategory(row) && (
            <>
              <CircularIconButton
                icon={editIcon}
                onClick={() => handleEditCategory(row)}
                aria-label="Modifier"
              />
              <CircularIconButton
                icon={trashIcon}
                onClick={() => handleDeleteCategory(row)}
                aria-label="Supprimer"
              />
            </>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Sticky Header Section */}
      <Box
        className="categories-sticky-header"
        sx={{
          position: 'sticky',
          top: '0px',
          zIndex: 1000,
          backgroundColor: isStuck ? '#F8F8F8' : 'transparent',
          borderBottom: isStuck ? '1px solid #E5E8EF' : 'none',
          transition: 'background-color 0.2s ease, border-bottom 0.2s ease'
        }}
      >
        <Box sx={stickyOuterSx}>
          <Box sx={{ paddingTop: '24px', paddingBottom: '8px' }}>
            {/* Page Title & Create Button */}
            <Box sx={{ marginBottom: '8px', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h1 
                style={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '28px',
                  lineHeight: '32px',
                  letterSpacing: '0px',
                  color: '#38485C',
                  opacity: 0.88,
                  textTransform: 'none',
                  margin: 0
                }}
              >
                Categories
              </h1>

              {user?.role === 'MANAGER' && (
                <Button
                  variant="contained"
                  startIcon={<img src={plusIcon} alt="plus" style={{ width: '16px', height: '16px' }} />}
                  onClick={handleCreateCategory}
                  sx={{
                    height: '40px',
                    backgroundColor: '#027BC6',
                    boxShadow: '0px 20px 80px #00000011',
                    borderRadius: '12px',
                    opacity: 1,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: '#025a9c'
                    }
                  }}
                >
                  Créer une nouvelle catégorie
                </Button>
              )}
            </Box>

            {/* Filters */}
            <Box sx={{ width: '100%', overflow: 'visible', position: 'relative', zIndex: 1001 }}>
              <CategoryFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onReset={handleResetFilters}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={contentContainerSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: '16px',
              paddingBottom: '8px',
              paddingLeft: '16px',
              paddingRight: '16px',
              color: '#919191',
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: 'Montserrat'
            }}
          >
            {columns.map((column, index) => {
              const isActionsColumn = column.key === 'actions';
              const isSortable = column.sortable && handleSort;
              const isCurrentlySorted = sortBy === column.key;
              
              return (
                <Box
                  key={index}
                  sx={{
                    paddingLeft: '24px',
                    paddingRight: '24px',
                    textAlign: isActionsColumn ? 'center' : 'left',
                    ...(isActionsColumn 
                      ? { display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }
                      : column.width
                        ? { flex: 'none', width: column.width }
                        : { flex: 1, minWidth: '150px' }),
                    ...(isActionsColumn && column.width ? { width: column.width, flexShrink: 0, flexGrow: 0 } : {}),
                    ...(column.width && !isActionsColumn ? { width: column.width } : {})
                  }}
                >
                  {isSortable ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer',
                        '&:hover': { color: '#027BC6' },
                        transition: 'color 0.2s',
                        color: isCurrentlySorted ? '#027BC6' : '#919191'
                      }}
                      onClick={() => handleSort(column.key, sortBy === column.key && sortDirection === 'ASC' ? 'DESC' : 'ASC')}
                    >
                      <span>{column.label}</span>
                      {isCurrentlySorted && (
                        <img
                          src={sortDirection === 'ASC' ? chevronUpIcon : chevronDownIcon}
                          alt={sortDirection === 'ASC' ? 'ascending' : 'descending'}
                          style={{
                            width: '12px',
                            height: '12px',
                            filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(188deg) brightness(89%) contrast(101%)'
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    column.label
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Categories List */}
      <Box sx={{ ...contentContainerSx, paddingBottom: '32px' }}>
        <Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            <Rows
              data={getPaginatedData()}
              columns={columns}
              pagination={{
                page: pagination.page,
                size: pagination.size,
                total: pagination.total,
                onPageChange: (newPage) => setPagination(prev => ({ ...prev, page: newPage })),
                onSizeChange: (newSize) => setPagination(prev => ({ ...prev, size: newSize, page: 0 }))
              }}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSort={handleSort}
              emptyMessage="Aucune catégorie trouvée"
              hideHeader={true}
              containerPadding={0}
            />
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, category: null })}
        onConfirm={deleteDialog.mode === 'disable' ? confirmDisable : confirmDelete}
        type={deleteDialog.mode === 'disable' ? 'deactivate' : 'delete'}
        title={deleteDialog.mode === 'disable' ? 'Désactiver la catégorie' : 'Supprimer la catégorie'}
        messageComponent={deleteDialog.mode === 'disable' ? (
          <span>
            Cette catégorie a au moins une transaction. Elle sera{' '}
            <span style={{ color: '#007BC6' }}>désactivée</span> au lieu d'être supprimée.
            <br /><br />
            Êtes-vous sûr de vouloir désactiver la catégorie{' '}
            <span style={{ color: '#007BC6' }}>{deleteDialog.category?.label}</span>
            {' '}?
          </span>
        ) : undefined}
        message={deleteDialog.mode === 'disable' ? undefined : (
          <>
            Êtes-vous sûr de vouloir supprimer la catégorie{' '}
            <span style={{ color: '#007BC6' }}>{deleteDialog.category?.label}</span>
            {' '}? Cette action est irréversible.
          </>
        )}
        confirmText={deleteDialog.mode === 'disable' ? 'Désactiver' : 'Supprimer'}
        cancelText="Annuler"
        loading={false}
        hasExtendedContent={deleteDialog.mode === 'disable'}
      />

      {/* Edit Confirmation Dialog */}
      <ConfirmationDialog
        open={editConfirmDialog.open}
        onClose={() => setEditConfirmDialog({ open: false, changes: null })}
        onConfirm={confirmEditChanges}
        type="confirm"
        title="Confirmer les modifications"
        message="Êtes-vous sûr de vouloir enregistrer ces modifications ?"
        loading={actionLoading}
      />

      {/* Status Change Confirmation Dialog */}
      <ConfirmationDialog
        open={statusDialog.open}
        onClose={() => setStatusDialog({ open: false, category: null, action: null, formData: null })}
        onConfirm={confirmStatusChange}
        type={statusDialog.action === 'activate' ? 'activate' : 'deactivate'}
        title={statusDialog.action === 'activate' ? 'Activer la catégorie' : 'Désactiver la catégorie'}
        messageComponent={
          <span>
            Êtes-vous sûr de vouloir{' '}
            {statusDialog.action === 'activate' ? 'activer' : 'désactiver'} la catégorie{' '}
            <span style={{ color: '#027BC6' }}>{statusDialog.category?.label}</span>
            {' '}?
          </span>
        }
        confirmText={statusDialog.action === 'activate' ? 'Activer' : 'Désactiver'}
        cancelText="Annuler"
        loading={false}
      />

      {/* Notification Change Confirmation Dialog */}
      <ConfirmationDialog
        open={notificationDialog.open}
        onClose={() => setNotificationDialog({ open: false, category: null, action: null })}
        onConfirm={confirmNotificationChange}
        type={notificationDialog.action === 'activate' ? 'activate' : 'deactivate'}
        title={notificationDialog.action === 'activate' ? 'Activer les notifications' : 'Désactiver les notifications'}
        messageComponent={
          <span>
            Êtes-vous sûr de vouloir{' '}
            {notificationDialog.action === 'activate' ? 'activer les notifications' : 'désactiver les notifications'} pour{' '}
            <span style={{ color: '#027BC6' }}>{notificationDialog.category?.label}</span>
            {' '}?
          </span>
        }
        confirmText={notificationDialog.action === 'activate' ? 'Activer' : 'Désactiver'}
        cancelText="Annuler"
        loading={false}
      />

      {/* Pointage Change Confirmation Dialog */}
      <ConfirmationDialog
        open={pointageDialog.open}
        onClose={() => setPointageDialog({ open: false, category: null, action: null })}
        onConfirm={confirmPointageChange}
        type={pointageDialog.action === 'activate' ? 'activate' : 'deactivate'}
        title={pointageDialog.action === 'activate' ? 'Activer le pointage' : 'Désactiver le pointage'}
        messageComponent={
          <span>
            Êtes-vous sûr de vouloir{' '}
            {pointageDialog.action === 'activate' ? 'activer le pointage' : 'désactiver le pointage'} pour{' '}
            <span style={{ color: '#027BC6' }}>{pointageDialog.category?.label}</span>
            {' '}?
          </span>
        }
        confirmText={pointageDialog.action === 'activate' ? 'Activer' : 'Désactiver'}
        cancelText="Annuler"
        loading={false}
      />

      {/* Create Category Modal */}
      <CreationModal
        open={createModal.open}
        onClose={() => setCreateModal({ open: false, errors: {} })}
        onSubmit={handleSubmitCreateCategory}
        title="Créer une nouvelle catégorie"
        fields={getCategoryFields}
        submitButtonText="Créer une nouvelle catégorie"
        loading={actionLoading}
        toggleConfig={{
          field: 'active',
          activeLabel: 'Actif',
          inactiveLabel: 'Inactif'
        }}
        externalErrors={createModal.errors}
        initialData={useMemo(() => ({ 
          label: '', 
          caisseFactor: '', 
          coffreFactor: '', 
          caisseIds: [], 
          notifyManager: '', 
          requiresPointage: '', 
          active: true 
        }), [])}
      />

      {/* Edit Category Modal */}
      <CreationModal
        open={editModal.open}
        onClose={() => setEditModal({ open: false, category: null, errors: {} })}
        onSubmit={handleSubmitEditCategory}
        title="Modifier la catégorie"
        fields={editCategoryFields}
        submitButtonText="Enregistrer"
        loading={actionLoading}
        toggleConfig={{
          field: 'active',
          activeLabel: 'Actif',
          inactiveLabel: 'Inactif'
        }}
        showLabels={true}
        initialData={useMemo(() => editModal.category ? {
          label: editModal.category.label || '',
          caisseFactor: editModal.category.caisseFactor !== undefined ? editModal.category.caisseFactor : '',
          coffreFactor: editModal.category.coffreFactor !== undefined ? editModal.category.coffreFactor : '',
          notifyManager: editModal.category.notifyManager !== undefined ? editModal.category.notifyManager : false,
          requiresPointage: editModal.category.requiresPointage !== undefined ? editModal.category.requiresPointage : false,
          active: editModal.category.active !== undefined ? editModal.category.active : true
        } : {}, [editModal.category])}
      />

      {/* Snackbar */}
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default Categories;